'use client';

import Link from 'next/link';
import type { Plant } from '@/data/plants';
import { useUserData } from '@/context/UserDataContext';
import SeedStartingDateResults, { hasSeedStartingDateRows } from '@/components/SeedStartingDateResults';
import { calculateSeedStartingDates } from '@/lib/seedStartingCalculator';
import { getPlantingReadiness } from '@/lib/plantingReadiness';

export default function PlantStartDates({ plant }: { plant: Plant }) {
  const { zone, weatherData } = useUserData();
  const calculation = calculateSeedStartingDates(plant, zone);
  const hasRows = hasSeedStartingDateRows(calculation);
  const readinessWindow = calculation.transplant ?? calculation.directSow ?? null;
  const readiness = getPlantingReadiness({
    plant,
    zone,
    weather: weatherData,
    calculatedWindow: readinessWindow,
  });

  if (!hasRows && zone) return null;

  return (
    <div className="pt-4 mt-4 border-t border-sand">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
        <div>
          <p className="font-body font-semibold text-[12px] text-sage uppercase tracking-wider">
            Start Dates (Your Location)
          </p>
          <p className="font-body text-[13px] text-sage mt-0.5">
            Average dates use your saved zone; readiness also checks your forecast when available.
          </p>
        </div>
        <Link
          href={`/seed-starting-calculator?plant=${encodeURIComponent(plant.id)}`}
          className="inline-flex self-start items-center justify-center rounded-lg border border-sage px-3 py-1.5 font-body text-[13px] font-semibold text-sage hover:bg-light-sage transition-colors"
        >
          Open Seed Starting Date Calculator
        </Link>
      </div>

      <SeedStartingDateResults calculation={calculation} readiness={readiness} compact />
    </div>
  );
}
